import { useEffect, useRef, useState } from "react";
import type { MouseEvent } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";

import {
  documentLinePreviewUrl,
  documentPdfUrl,
  fetchDocumentMediaToken,
} from "../api/documents";
import { isDemoMode } from "../lib/env";
import { useEscapeKey } from "../lib/useEscapeKey";
import {
  SourceIcon,
  sourceBadgeClassName,
  type SourceBadgeKind,
} from "./SourceBadge";

// One short-lived token is shared across every PDF link/preview on the page.
// react-query dedupes the fetch by key and refreshes it before the server TTL
// (15 min) so already-rendered hrefs stay valid while a tab is open.
const MEDIA_TOKEN_QUERY_KEY = ["documents", "media-token"] as const;
const MEDIA_TOKEN_REFRESH_MS = 10 * 60_000;

export function useDocumentMediaTokenState(enabled: boolean) {
  const query = useQuery({
    queryKey: MEDIA_TOKEN_QUERY_KEY,
    queryFn: fetchDocumentMediaToken,
    enabled,
    staleTime: MEDIA_TOKEN_REFRESH_MS,
    refetchInterval: MEDIA_TOKEN_REFRESH_MS,
    gcTime: 15 * 60_000,
  });
  return { token: query.data?.token ?? null, isError: query.isError, retry: query.refetch };
}

function useDocumentMediaToken(enabled: boolean): string | null {
  return useDocumentMediaTokenState(enabled).token;
}

type SourceVariant = Extract<SourceBadgeKind, "pdf" | "ugl" | "datanorm">;

function sourceVariant(sourceKind: string | null | undefined): SourceVariant {
  const normalized = sourceKind?.toLowerCase() ?? "";
  if (normalized.startsWith("ugl")) return "ugl";
  if (normalized.startsWith("datanorm")) return "datanorm";
  return "pdf";
}

const SOURCE_WORD: Record<SourceVariant, string> = {
  pdf: "PDF",
  ugl: "UGL",
  datanorm: "DATANORM",
};

function openLabel(variant: SourceVariant): string {
  return variant === "pdf" ? "Ganzes PDF öffnen" : "Ganzes Dokument öffnen";
}

function IconEye({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M2.04 12.32a1.01 1.01 0 010-.64C3.42 7.51 7.36 4.5 12 4.5s8.57 3.01 9.96 7.18c.07.21.07.43 0 .64C20.58 16.49 16.64 19.5 12 19.5s-8.57-3.01-9.96-7.18z" />
      <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function IconExternal({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5" />
      <path d="M15.75 3h5.25v5.25M21 3l-9 9" />
    </svg>
  );
}

function IconCopy({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </svg>
  );
}

function IconCheck({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

async function copyTextToClipboard(value: string): Promise<boolean> {
  if (fallbackCopyText(value)) return true;
  if (navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(value);
      return true;
    } catch {
      return false;
    }
  }
  return false;
}

function fallbackCopyText(value: string): boolean {
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  textarea.style.top = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
}

type Props = {
  docId: string | null;
  docNumber?: string | null;
  linePos?: string | number | null;
  pageNo?: number | null;
  sourceKind?: string | null;
  label?: string;
  className?: string;
  stopPropagation?: boolean;
  compact?: boolean;
  stacked?: boolean;
  /** The document header already provides the full PDF link. */
  previewOnly?: boolean;
  previewActionLabel?: string;
  openActionLabel?: string;
};

export default function DocumentPdfActions({
  docId,
  docNumber,
  linePos,
  pageNo,
  sourceKind,
  label,
  className = "",
  stopPropagation = false,
  compact = false,
  stacked = false,
  previewOnly = false,
  previewActionLabel,
  openActionLabel,
}: Props) {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ left: 16, top: 16 });
  const [copyFeedback, setCopyFeedback] = useState(false);
  const variant = sourceVariant(sourceKind);
  const mediaToken = useDocumentMediaToken(
    Boolean(docId) && variant === "pdf" && !isDemoMode(),
  );
  const sourceWord = SOURCE_WORD[variant];
  const chipText = label ?? docNumber ?? sourceWord;
  const resolvedPreviewActionLabel = previewActionLabel ?? "Vorschau";
  const resolvedOpenActionLabel = openActionLabel ?? openLabel(variant);
  const popoverDocumentLabel = docNumber ?? docId ?? sourceWord;
  const copyValue = docNumber ?? docId ?? "";
  const hasLineTarget = linePos !== null && linePos !== undefined;
  const compactPreviewLabel = `Vorschau für Beleg ${
    docNumber ?? docId ?? sourceWord
  }${hasLineTarget ? `, Position ${String(linePos)}` : ""}`;
  const compactOpenLabel = `${resolvedOpenActionLabel} für Beleg ${chipText}`;
  const hasDocumentMedia = Boolean(docId && (variant === "pdf" || isDemoMode()));
  const pdfUrl = hasDocumentMedia
    ? documentPdfUrl(docId ?? "", pageNo, mediaToken)
    : "#";
  const previewEnabled = Boolean(hasDocumentMedia && hasLineTarget && previewOpen);
  useEscapeKey(() => setPreviewOpen(false), previewEnabled);

  useEffect(() => {
    if (!previewEnabled) return;

    function handlePointerDown(event: globalThis.MouseEvent) {
      const target = event.target as Node;
      if (popoverRef.current?.contains(target)) return;
      if (buttonRef.current?.contains(target)) return;
      setPreviewOpen(false);
    }

    document.addEventListener("mousedown", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
    };
  }, [previewEnabled]);

  useEffect(() => {
    if (!copyFeedback) return;
    const timeout = window.setTimeout(() => setCopyFeedback(false), 1500);
    return () => window.clearTimeout(timeout);
  }, [copyFeedback]);

  useEffect(() => {
    if (!previewOpen) setCopyFeedback(false);
  }, [previewOpen]);

  // Ohne Dokument nur den Text zeigen. Im Demo-Mode liefern die URL-Helfer
  // statische, wasserzeichenbehaftete Demo-Dateien (kein Backend/Token nötig).
  if (!docId) {
    return <span className={className}>{chipText}</span>;
  }

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    if (stopPropagation) event.stopPropagation();
  }

  function togglePreview(event: MouseEvent<HTMLButtonElement>) {
    if (stopPropagation) event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const maxLeft = window.scrollX + Math.max(12, window.innerWidth - 680);
    const maxTop = window.scrollY + Math.max(12, window.innerHeight - 460);
    setPopoverPosition({
      left: Math.min(
        Math.max(window.scrollX + 12, window.scrollX + rect.left),
        maxLeft,
      ),
      top: Math.min(
        Math.max(window.scrollY + 12, window.scrollY + rect.bottom + 8),
        maxTop,
      ),
    });
    setPreviewOpen((open) => !open);
  }

  function copyDocumentLabel(event: MouseEvent<HTMLButtonElement>) {
    event.stopPropagation();
    if (!copyValue) return;
    setCopyFeedback(true);
    void copyTextToClipboard(copyValue);
  }

  return (
    <span
      className={`inline-flex ${
        stacked
          ? "flex-col items-start gap-0.5 whitespace-nowrap"
          : compact
            ? "items-center gap-1.5 whitespace-nowrap"
            : "items-center flex-wrap gap-x-2 gap-y-1"
      } ${className}`}
    >
      {hasLineTarget && hasDocumentMedia ? (
        <button
          ref={buttonRef}
          type="button"
          onClick={togglePreview}
          className={
            compact && !stacked
              ? "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
              : "inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 transition-colors hover:text-slate-800 hover:underline"
          }
          title={previewActionLabel ?? "Position im Beleg anzeigen"}
          aria-label={
            compact || stacked
              ? previewActionLabel ?? compactPreviewLabel
              : resolvedPreviewActionLabel
          }
        >
          <IconEye
            className={compact && !stacked ? "h-4 w-4" : "h-3.5 w-3.5"}
          />
          {compact && !stacked ? null : resolvedPreviewActionLabel}
        </button>
      ) : null}
      {previewOnly ? null : hasDocumentMedia ? (
        <a
          href={documentPdfUrl(docId, null, mediaToken)}
          target="_blank"
          rel="noreferrer"
          onClick={handleClick}
          className={
            compact && !stacked
              ? "font-mono text-xs text-slate-600 transition-colors hover:text-slate-900 hover:underline"
              : `${sourceBadgeClassName({ interactive: true, size: "xs" })} tracking-wide`
          }
          title={resolvedOpenActionLabel}
          aria-label={
            compact || stacked ? compactOpenLabel : resolvedOpenActionLabel
          }
        >
          {compact && !stacked ? (
            chipText
          ) : (
            <>
              <SourceIcon kind={variant} className="h-3.5 w-3.5" />
              {openActionLabel ?? chipText}
              <IconExternal className="h-3 w-3 opacity-70" />
            </>
          )}
        </a>
      ) : (
        <span
          className={`${sourceBadgeClassName({ size: "xs" })} tracking-wide`}
          title={`${sourceWord}-Nachweis`}
        >
          <SourceIcon kind={variant} className="h-3.5 w-3.5" />
          {chipText}
        </span>
      )}
      {previewOpen && hasLineTarget && hasDocumentMedia
        ? createPortal(
            <div
              ref={popoverRef}
              role="dialog"
              aria-label="PDF-Vorschau"
              className="absolute z-50 flex max-h-[calc(100vh-1.5rem)] w-[min(42rem,calc(100vw-1.5rem))] flex-col rounded-md border border-slate-200 bg-white p-2 shadow-xl"
              style={popoverPosition}
            >
              <div className="mb-2 flex items-center justify-between gap-2 px-1">
                <div className="min-w-0">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <div className="truncate text-xs font-medium text-slate-800">
                      {popoverDocumentLabel}
                    </div>
                    <button
                      type="button"
                      onClick={copyDocumentLabel}
                      disabled={!copyValue}
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-40"
                      title={copyFeedback ? "Kopiert" : "Belegnummer kopieren"}
                      aria-label={
                        copyFeedback ? "Belegnummer kopiert" : "Belegnummer kopieren"
                      }
                    >
                      {copyFeedback ? (
                        <IconCheck className="h-3 w-3" />
                      ) : (
                        <IconCopy className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                  <div className="text-[11px] text-slate-500">
                    Zeile {String(linePos)}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className={`${sourceBadgeClassName({ interactive: true, size: "xs" })} tracking-wide`}
                  >
                    <SourceIcon kind={variant} className="h-3.5 w-3.5" />
                    {resolvedOpenActionLabel}
                    <IconExternal className="h-3 w-3 opacity-70" />
                  </a>
                  <button
                    type="button"
                    onClick={() => setPreviewOpen(false)}
                    className="h-6 w-6 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    aria-label="Vorschau schließen"
                  >
                    ×
                  </button>
                </div>
              </div>
              <div className="min-h-20 overflow-auto rounded border border-slate-100 bg-slate-50">
                {mediaToken || isDemoMode() ? (
                  <img
                    src={documentLinePreviewUrl(docId, linePos, 2, mediaToken)}
                    alt={`Position Zeile ${String(linePos)}`}
                    className="block w-full"
                  />
                ) : (
                  <div className="p-3 text-xs text-slate-500">
                    Lade Vorschau …
                  </div>
                )}
              </div>
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}
